import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';

/**
 * Trade Finance Dokumententracker
 * Matrix-Ansicht: jeder Deal = eine Zeile, Dokument-Status = Spalten
 * Direktes Inline-Update der Status ohne separaten Edit-Dialog
 */

const DOC_FIELDS = [
  { key: 'loi_status',  label: 'LOI',   abbr: 'LOI',  help: 'Letter of Intent' },
  { key: 'spa_status',  label: 'SPA',   abbr: 'SPA',  help: 'Sales & Purchase Agreement' },
  { key: 'lc_status',   label: 'LC',    abbr: 'LC',   help: 'Letter of Credit / SBLC' },
  { key: 'bl_status',   label: 'BL',    abbr: 'BL',   help: 'Bill of Lading' },
];

const STATUSES = [
  { key: 'pending',   label: 'Ausstehend', color: 'var(--text3)' },
  { key: 'received',  label: 'Erhalten',   color: '#3b82f6' },
  { key: 'approved',  label: 'Bestätigt',  color: '#22c55e' },
  { key: 'rejected',  label: 'Abgelehnt',  color: '#ef4444' },
];

const STAGE_COLORS = {
  initial: 'var(--text3)', kyc: '#f59e0b', loi: '#3b82f6', spa: '#8b5cf6',
  execution: '#ec4899', settlement: '#22c55e', billing: '#10b981',
  completed: '#059669', cancelled: '#ef4444',
};

function statusMeta(key) {
  return STATUSES.find(s => s.key === key) || STATUSES[0];
}

function StatusCell({ dealId, field, value, onUpdate }) {
  const [saving, setSaving] = useState(false);
  const meta = statusMeta(value);

  const cycle = async () => {
    const idx = STATUSES.findIndex(s => s.key === value);
    const next = STATUSES[(idx + 1) % STATUSES.length].key;
    setSaving(true);
    try {
      await API.put(`/deals/${dealId}`, { [field]: next });
      onUpdate(dealId, field, next);
    } finally { setSaving(false); }
  };

  return (
    <td style={{ padding: '8px 10px', textAlign: 'center', verticalAlign: 'middle' }}>
      <button onClick={cycle} disabled={saving}
        title="Klicken zum Weiterschalten"
        style={{ cursor: 'pointer', border: 'none', borderRadius: 6,
          padding: '3px 10px', fontSize: 11, fontWeight: 700,
          background: meta.color + '22', color: meta.color,
          opacity: saving ? 0.5 : 1, transition: 'all .15s', whiteSpace: 'nowrap' }}>
        {saving ? '…' : meta.label}
      </button>
    </td>
  );
}

// ── Summary Stats ──────────────────────────────────────────────────────────────

function DocSummary({ deals }) {
  const total = deals.length;
  if (!total) return null;

  const stats = DOC_FIELDS.map(f => {
    const approved = deals.filter(d => d[f.key] === 'approved').length;
    const pct = Math.round((approved / total) * 100);
    return { ...f, approved, pct };
  });

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
      {stats.map(s => (
        <div key={s.key} className="card" style={{ textAlign: 'center', padding: '14px 16px', margin: 0 }}>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{s.help}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: s.pct === 100 ? '#22c55e' : s.pct > 50 ? '#f59e0b' : 'var(--text2)' }}>
            {s.pct}%
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{s.approved} / {total} bestätigt</div>
          {/* mini progress */}
          <div style={{ height: 4, background: 'var(--bg-surface)', borderRadius: 2, marginTop: 8 }}>
            <div style={{ height: 4, width: `${s.pct}%`, background: s.pct === 100 ? '#22c55e' : '#f59e0b', borderRadius: 2, transition: 'width .4s' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function TradeFinancePlatformPage() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState('');
  const [q, setQ] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = { limit: 500 };
      const r = await API.get('/deals', { params });
      setDeals(r.data);
    } catch { setDeals([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = (dealId, field, newVal) => {
    setDeals(prev => prev.map(d => d.id === dealId ? { ...d, [field]: newVal } : d));
  };

  const filtered = deals.filter(d => {
    if (!showCompleted && ['completed', 'cancelled'].includes(d.stage)) return false;
    if (stageFilter && d.stage !== stageFilter) return false;
    if (q) {
      const s = q.toLowerCase();
      return d.title?.toLowerCase().includes(s)
        || d.reference?.toLowerCase().includes(s)
        || d.buyer_name?.toLowerCase().includes(s)
        || d.seller_name?.toLowerCase().includes(s);
    }
    return true;
  });

  const activeDeals = deals.filter(d => !['completed', 'cancelled'].includes(d.stage));
  const uniqueStages = [...new Set(deals.map(d => d.stage))];

  return (
    <div className="page-content">
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Trade Finance Dokumententracker</h1>
          <p>Dokument-Status pro Deal — klicke einen Status zum Weiterschalten</p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={load}>⟳ Aktualisieren</button>
      </div>

      {/* Doc Completion Summary */}
      {activeDeals.length > 0 && <DocSummary deals={activeDeals} />}

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="form-input" style={{ width: 220 }} placeholder="Deal suchen…"
          value={q} onChange={e => setQ(e.target.value)} />
        <select className="form-input" style={{ width: 160 }} value={stageFilter}
          onChange={e => setStageFilter(e.target.value)}>
          <option value="">Alle Stages</option>
          {uniqueStages.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text3)', cursor: 'pointer' }}>
          <input type="checkbox" checked={showCompleted} onChange={e => setShowCompleted(e.target.checked)} />
          Abgeschlossen anzeigen
        </label>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text3)' }}>
          {filtered.length} Deals
        </span>
      </div>

      {loading && <div className="loading">Lade Deals…</div>}

      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
          <div>Keine Deals gefunden.</div>
        </div>
      )}

      {/* Document Matrix Table */}
      {!loading && filtered.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="tbl" style={{ minWidth: 700 }}>
            <thead>
              <tr>
                <th style={{ minWidth: 80 }}>Stage</th>
                <th style={{ minWidth: 200 }}>Deal</th>
                <th style={{ minWidth: 130 }}>Buyer / Seller</th>
                {DOC_FIELDS.map(f => (
                  <th key={f.key} style={{ minWidth: 110, textAlign: 'center' }} title={f.help}>
                    {f.abbr}
                  </th>
                ))}
                <th style={{ minWidth: 90, textAlign: 'center' }}>Komplett</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(deal => {
                const stageColor = STAGE_COLORS[deal.stage] || 'var(--text3)';
                const allDone = DOC_FIELDS.every(f => deal[f.key] === 'approved');
                const doneCnt = DOC_FIELDS.filter(f => deal[f.key] === 'approved').length;

                return (
                  <tr key={deal.id}>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px',
                        borderRadius: 4, fontSize: 11, fontWeight: 700,
                        background: stageColor + '22', color: stageColor }}>
                        {deal.stage}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{deal.title}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{deal.reference}</div>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text3)' }}>
                      {[deal.buyer_name, deal.seller_name].filter(Boolean).join(' / ') || '—'}
                    </td>
                    {DOC_FIELDS.map(f => (
                      <StatusCell
                        key={f.key}
                        dealId={deal.id}
                        field={f.key}
                        value={deal[f.key] || 'pending'}
                        onUpdate={handleUpdate}
                      />
                    ))}
                    <td style={{ textAlign: 'center', padding: '8px 10px' }}>
                      {allDone ? (
                        <span style={{ color: '#22c55e', fontWeight: 700, fontSize: 16 }}>✓</span>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--text3)' }}>{doneCnt}/{DOC_FIELDS.length}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      <div style={{ marginTop: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>Status:</span>
        {STATUSES.map(s => (
          <span key={s.key} style={{ fontSize: 11, color: s.color, fontWeight: 600 }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
              background: s.color, marginRight: 4 }} />
            {s.label}
          </span>
        ))}
        <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 8 }}>
          ← Klick auf Status schaltet ihn weiter
        </span>
      </div>
    </div>
  );
}
